import * as CL from '@design-system/component-library';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouteLoaderData } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { nextMsg, submitOrderMsg, t } from '../../common/i18n/index.js';
import {
  VAK_PRODUCT_TYPE,
  getProductDeliveryTypesInCart,
  isEppDevicePresentInCart,
} from '../../common/utils/checkoutUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { isPOBoxAddress } from '../../common/utils/validationUtils.js';
import { DeliveryType, OnlineOrder } from '../../generated/api/models.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { cartContainsDirectDeliveryItems, isAnyCartItemBackordered } from '../DeviceCheckout/deviceCheckoutUtils.js';
import { Icon } from '../Icon/Icon';
import { POBoxWarningModal } from '../Modal/POBoxWarningModal.js';
import { DeviceCheckoutAddressConfirmation } from './DeviceCheckoutAddressConfirmation.js';
import { DELIVERY_MODES, DeliveryModeType, validateDeliveryDetails } from './DeviceCheckoutDeliveryDetailsUtils.js';
import { DeviceCheckoutDeliveryAddressSection } from './components/DeviceCheckoutDeliveryAddressSection.js';
import { DeviceCheckoutDeliveryMethodsSelectionList } from './components/DeviceCheckoutDeliveryMethodsSelectionList.js';
import { DeviceCheckoutOrderShipmentTypeSelection } from './components/DeviceCheckoutOrderShipmentTypeSelection.js';
import type { AddressConfirmationRef } from './DeviceCheckoutAddressConfirmation.js';
import type {
  DeliveryMode,
  DeviceCheckoutDeliveryDetailsBaseProps,
  DeviceCheckoutDeliveryDetailsType,
  ErrorType,
} from './DeviceCheckoutDeliveryDetailsUtils.js';
import type { CheckoutRootLoaderData, PunchoutCheckoutLoaderData } from '../../common/loaders.js';
import type { State } from '../../selfservice/common/store.js';

import './DeviceCheckoutDeliveryDetails.scss';

export interface DeviceCheckoutDeliveryDetailsProps extends DeviceCheckoutDeliveryDetailsBaseProps {
  saveDeliveryDetailsAndNext: (value: DeviceCheckoutDeliveryDetailsType) => void;
  onSubmitOrder?: (deliveryDetails: DeviceCheckoutDeliveryDetailsType) => void;
  showConfirmAddress?: boolean;
  hasPreInstallation?: boolean;
  setRefCallback?: (key: string, ref: HTMLElement | null) => void;
  addressConfirmationRef?: React.RefObject<AddressConfirmationRef | null>;
  shipmentTypeSelectionEnabled?: boolean;
  showDeliveryDetailsStep?: boolean;
}

const isPickupPointRequired = (details: DeviceCheckoutDeliveryDetailsType): boolean => {
  return [
    DeliveryType.MATKAHUOLTO_LAHELLA_PAKETTI,
    DeliveryType.POSTI_SMARTPOST,
    DeliveryType.POSTI_SMARTPOST_EXPRESS,
  ].includes(details.deliveryMethodType);
};

export const DeviceCheckoutDeliveryDetails = (props: DeviceCheckoutDeliveryDetailsProps) => {
  const {
    saveDeliveryDetailsToState,
    deliveryDetails,
    onSubmitOrder,
    showConfirmAddress,
    hasPreInstallation,
    setRefCallback,
    addressConfirmationRef,
    shipmentTypeSelectionEnabled = true, // Default to true for non-punchout flows
    showDeliveryDetailsStep = false,
  } = props;
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isAddressChanged, setIsAddressChanged] = useState(false);
  const [validationErrorsList, setValidationErrorsList] = useState<ErrorType>({});

  const [shipmentType, setShipmentType] = useState<OnlineOrder.ShipmentTypeEnum>(
    OnlineOrder.ShipmentTypeEnum.TOTAL_DELIVERY
  );
  const [selectedDeliveryMode, setSelectedDeliveryMode] = useState(DELIVERY_MODES[0]);
  const [showPOBoxWarningModal, setShowPOBoxWarningModal] = useState(false);

  const deviceCheckout = useSelector((state: State) => state.deviceCheckout, deepEqual);
  const isOrderInProgress = useSelector((state: State) => Boolean(state.selfservice?.onlineOrders?.saving), deepEqual);
  // DeviceCheckoutDeliveryDetails is shared between the employee and punchout checkout flows, which supply
  // online models via different route loaders, so both are consulted here.
  const employeeLoaderData = useRouteLoaderData('checkoutRootEmployee') as CheckoutRootLoaderData | undefined;
  const punchoutLoaderData = useRouteLoaderData('checkoutRootPunchout') as PunchoutCheckoutLoaderData | undefined;
  const onlineModels = employeeLoaderData?.models ?? punchoutLoaderData?.models;
  const cartItems = useSelector((state: State) => state.deviceCheckout?.cartItems || [], deepEqual);
  const loadingDeliveryMethods = useSelector(
    (state: State) => state.deviceCheckout?.deliveryMethods?.isLoading || false
  );
  const companyInfo = useSessionData().companyInfo;

  const errors = deviceCheckout?.errors;
  const listOfDeliveryMethods = deviceCheckout?.deliveryMethods?.lisOfDeliveryMethods;
  const isDirectDelivery = cartContainsDirectDeliveryItems(cartItems, onlineModels);
  const showShipmentType = cartItems?.length > 1 && isAnyCartItemBackordered(cartItems, onlineModels);
  const companyName = companyInfo ? companyInfo.companyName : '';
  const eppDevicePresentInCart = isEppDevicePresentInCart(cartItems);

  useEffect(() => {
    const currentShipmentType = deliveryDetails.shipmentType;
    const newShipmentType =
      (isDirectDelivery && OnlineOrder.ShipmentTypeEnum.PARTIAL_DELIVERY) ||
      (!currentShipmentType && OnlineOrder.ShipmentTypeEnum.TOTAL_DELIVERY) ||
      undefined;

    if (newShipmentType && newShipmentType !== currentShipmentType) {
      setShipmentType(newShipmentType);
      saveDeliveryDetailsToState({
        ...deliveryDetails,
        shipmentType: newShipmentType,
      });
    }
  }, [cartItems, onlineModels, isDirectDelivery, saveDeliveryDetailsToState, deliveryDetails]);

  const handleDeliveryModeSelected = useCallback(
    (deliveryMode: DeliveryMode) => {
      const methods = deliveryMode?.getDeliveryMethods(listOfDeliveryMethods);
      const selectedMethod = methods?.[0];
      if (methods && selectedMethod) {
        saveDeliveryDetailsToState({
          ...deliveryDetails,
          deliveryMethodDescription: selectedMethod.createDescription(),
          deliveryMethodType: selectedMethod.type,
          deliveryPrice: selectedMethod.price,
          pickupPoint:
            selectedMethod.deliveryModeType === DeliveryModeType.ADDRESS ? undefined : deliveryDetails.pickupPoint,
        });
      }
    },
    [deliveryDetails, listOfDeliveryMethods, saveDeliveryDetailsToState]
  );

  return (
    <div className="of-device-checkout-delivery-details">
      {showPOBoxWarningModal && <POBoxWarningModal setShowModal={setShowPOBoxWarningModal} />}
      {shipmentTypeSelectionEnabled && (showShipmentType || isDirectDelivery) && (
        <DeviceCheckoutOrderShipmentTypeSelection
          shipmentType={shipmentType}
          isDirectDelivery={isDirectDelivery}
          hasPreInstallation={hasPreInstallation}
          backOrdered={showShipmentType}
          onShipmentTypeSelection={(orderType: OnlineOrder.ShipmentTypeEnum) => {
            setShipmentType(orderType);
            props.saveDeliveryDetailsToState({
              ...deliveryDetails,
              shipmentType: orderType,
            });
          }}
        />
      )}
      {!showDeliveryDetailsStep && (
        <>
          <DeviceCheckoutDeliveryAddressSection
            deliveryDetails={deliveryDetails}
            saveDeliveryDetailsToState={props.saveDeliveryDetailsToState}
            companyName={companyName}
            setIsEditingAddress={setIsEditingAddress}
            errors={errors}
            setIsAddressChanged={setIsAddressChanged}
            clearErrors={() => setValidationErrorsList({})}
          />

          {showConfirmAddress && (
            <DeviceCheckoutAddressConfirmation ref={addressConfirmationRef} setRefCallback={setRefCallback} />
          )}

          <DeviceCheckoutDeliveryMethodsSelectionList
            eppDevicePresentInCart={eppDevicePresentInCart}
            deliveryDetails={deliveryDetails}
            footerNote={props.footerNote}
            saveDeliveryDetailsToState={props.saveDeliveryDetailsToState}
            deliveryMode={selectedDeliveryMode}
            onDeliveryModeChosen={(deliveryModeSelection: DeliveryMode) => {
              setSelectedDeliveryMode(deliveryModeSelection);
              handleDeliveryModeSelected(deliveryModeSelection);
            }}
            listOfDeliveryMethods={listOfDeliveryMethods}
            isDirectDelivery={isDirectDelivery}
            hasVakProduct={getProductDeliveryTypesInCart(cartItems, onlineModels).includes(VAK_PRODUCT_TYPE)}
          />
        </>
      )}
      <CL.Button
        size="l"
        disabled={isOrderInProgress}
        loading={loadingDeliveryMethods}
        className={onSubmitOrder ? 'of-confirm-button' : 'of-next-button'}
        onClick={() => {
          if (showDeliveryDetailsStep) {
            props.saveDeliveryDetailsAndNext(deliveryDetails);
            if (onSubmitOrder) {
              onSubmitOrder(deliveryDetails);
            }
            return;
          }

          const validationErrors = validateDeliveryDetails(
            selectedDeliveryMode,
            listOfDeliveryMethods,
            isEditingAddress,
            deliveryDetails.pickupPoint,
            isPickupPointRequired(deliveryDetails),
            isAddressChanged
          );
          const hasErrors = Object.keys(validationErrors).length > 0;
          setValidationErrorsList(validationErrors);
          if (
            isPOBoxAddress(deliveryDetails.address?.postalCode) &&
            selectedDeliveryMode.type === DeliveryModeType.ADDRESS
          ) {
            setShowPOBoxWarningModal(true);
          } else if (!hasErrors) {
            props.saveDeliveryDetailsAndNext(deliveryDetails);
            if (onSubmitOrder) {
              onSubmitOrder(deliveryDetails);
            }
          }
        }}
      >
        {onSubmitOrder ? t.RZU4(submitOrderMsg) : t.F0MY(nextMsg)}
      </CL.Button>
      {Object.entries(validationErrorsList).map(([key, errorMsg]) => (
        <div key={key} className="of-validation-error-messages">
          <div className={dsClass.INPUTERROR} style={{ marginLeft: dsClass.MARGIN_2 }}>
            <Icon icon="error" size="s" type="regular" className={dsClass.INPUTERROR} color="red-600" />
            {errorMsg}
          </div>
        </div>
      ))}
    </div>
  );
};
